import {
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { Exercise } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Option, PagedResults } from "../../types/generics";
import { useToasts } from "../../contexts/toasts";
import ExerciseForm from "../../Components/Form/ExerciseForm";
import { TailSpin } from "react-loader-spinner";
import ExerciseItem from "../../Components/list-items/ExerciseItem";
import { debounce } from "lodash";
import { ConfigProvider, Pagination, Select, Space } from "antd";
import AppSearch from "../../common/AppSearch";
import itIT from "antd/lib/locale/it_IT";
import { compressImageFile } from "../../utils/imageCompression";

export default function ExercisesList() {
  const { showToast } = useToasts();
  const [exercises, setExercises] = useState<Exercise[]>();
  const [error, setError] = useState(false);
  const [total, setTotal] = useState<number>();
  const [selectedExercise, setSelectedExercise] = useState<Exercise>();
  const [muscleGroups, setMuscleGroups] = useState<Option[]>();
  const [motorPatterns, setMotorPatterns] = useState<Option[]>();
  const [addExerciseModal, setAddExerciseModal] = useState(false);
  const [deleteExerciseModal, setDeleteExerciseModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    freeText: string;
    motorPatternId?: string;
    muscleGroupId?: string;
  }>({
    pageSize: 20,
    currentPage: 1,
    freeText: "",
  });

  const initExercises = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);
    if (filters.freeText !== "") urlParams.append("freeText", filters.freeText);
    if (!!filters.muscleGroupId)
      urlParams.append("muscleGroupId", filters.muscleGroupId);
    if (!!filters.motorPatternId)
      urlParams.append("motorPatternId", filters.motorPatternId);
    Entities.getExercisesPaginated(urlParams).then(
      ({ data }) => {
        setExercises((data as PagedResults<Exercise>).items);
        setTotal((data as PagedResults<Exercise>).totalItems);
      },
      (err) => {
        showToast(
          "exercises-error",
          "Impossibile ottenere la lista degli Esercizi",
          "",
          "danger"
        );
        setExercises([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const initMuscleGroups = useCallback(() => {
    Entities.getMuscleGroups().then(
      ({ data }) => {
        setMuscleGroups(
          data.list.map((muscleGroup) => ({
            label: muscleGroup.muscleGroup,
            value: muscleGroup.id!,
          }))
        );
      },
      (err) => {
        showToast(
          "muscle-groups-error",
          "Impossibile ottenere la lista degli Gruppi Muscolari",
          "",
          "danger"
        );
        setMuscleGroups([]);
      }
    );
  }, [showToast]);

  const initMotorPatterns = useCallback(() => {
    Entities.getMotorPatterns().then(
      ({ data }) => {
        setMotorPatterns(
          data.list.map((motorPattern) => ({
            label: motorPattern.motorPattern,
            value: motorPattern.id!,
          }))
        );
      },
      (err) => {
        showToast(
          "motor-patterns-error",
          "Impossibile ottenere la lista degli Pattern Motori",
          "",
          "danger"
        );
        setMotorPatterns([]);
      }
    );
  }, [showToast]);

  const reset = useCallback(() => {
    setError(false);
    setExercises(undefined);
    setTotal(0);
    initExercises();
  }, [initExercises]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  useEffect(() => {
    initMuscleGroups();
    initMotorPatterns();
  }, [initMuscleGroups, initMotorPatterns]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: page,
    }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({
      ...previous,
      pageSize: size,
      currentPage: 1,
    }));
  }, []);

  const handleFilterChange = useCallback((text: string) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: 1,
      freeText: text,
    }));
  }, []);

  const handleFieldChange = useCallback(
    (field: string) => (evt: any) => {
      setFilters((previous) => ({
        ...previous,
        [field]: evt,
      }));
    },
    []
  );

  const debounceSearchChange = useMemo(
    () => debounce(handleFilterChange, 500),
    [handleFilterChange]
  );

  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    };
  }, [debounceSearchChange]);

  const handleEditExerciseModalClose = useCallback(() => {
    setAddExerciseModal(false);
    setSelectedExercise(undefined);
  }, []);

  const handleEditExerciseClick = useCallback(
    (exercise?: Exercise) => () => {
      setSelectedExercise(exercise);
      setAddExerciseModal(true);
    },
    []
  );

  const editExercise = useCallback(
    (exercise: Exercise) => () => {
      setSelectedExercise(exercise);
      setAddExerciseModal(true);
    },
    []
  );

  const openDeleteModal = useCallback(
    (exercise: Exercise) => () => {
      setSelectedExercise(exercise);
      setDeleteExerciseModal(true);
    },
    []
  );

  const renderExercises = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return exercises!.length === 0 ? (
      <Alert variant="info" className="py-2">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span className="ms-2">Nessun Esercizio trovato</span>
      </Alert>
    ) : (
      <Row>
        {exercises!.map((exercise, index) => (
          <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={index}>
            <ExerciseItem
              onEdit={editExercise(exercise)}
              onDelete={openDeleteModal(exercise)}
              exercise={exercise}
            />
          </Col>
        ))}
      </Row>
    );
  }, [exercises, error, editExercise, openDeleteModal]);

  const renderAsyncContent = useCallback(() => {
    return exercises ? (
      renderExercises()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#9c1419" radius="1" />
      </div>
    );
  }, [exercises, renderExercises]);

  const renderToolbar = useCallback(() => {
    return (
      <>
        <Row className="align-items-center">
          <Col>
            <h4 className="mb-0">Esercizi</h4>
          </Col>
          <Col xs={12} md="auto" className="d-none d-md-flex">
            <Space align="center">
              <AppSearch
                className="w-auto"
                placeholder="Cerca per nome..."
                onChange={debounceSearchChange}
              />
              <Select
                value={filters.muscleGroupId}
                placeholder="Cerca per gruppo muscolare..."
                className="w-100"
                allowClear
                onChange={handleFieldChange("muscleGroupId")}
              >
                {(muscleGroups || []).map((muscleGroup, index) => (
                  <Select.Option
                    key={index}
                    value={muscleGroup.value}
                    label={muscleGroup.label}
                  >
                    <span>{muscleGroup.label}</span>
                  </Select.Option>
                ))}
              </Select>
              <Select
                value={filters.motorPatternId}
                placeholder="Cerca per pattern motorio..."
                className="w-100"
                allowClear
                onChange={handleFieldChange("motorPatternId")}
              >
                {(motorPatterns || []).map((motorPatterns, index) => (
                  <Select.Option
                    key={index}
                    value={motorPatterns.value}
                    label={motorPatterns.label}
                  >
                    <span>{motorPatterns.label}</span>
                  </Select.Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col className="text-end">
            <button
              onClick={handleEditExerciseClick()}
              className="button-primary"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span className="d-none d-sm-inline ms-2">
                Aggiungi Esercizio
              </span>
            </button>
          </Col>
        </Row>
        <Row className="d-flex d-md-none my-3">
          <Col md="auto" className="mb-2" xs={12}>
            <AppSearch
              placeholder="Cerca per nome..."
              onChange={debounceSearchChange}
            />
          </Col>
          <Col md="auto" xs={12}>
            <Select
              value={filters.muscleGroupId}
              placeholder="Cerca per gruppo muscolare..."
              className="w-100"
              onChange={handleFieldChange("muscleGroupId")}
            >
              {(muscleGroups || []).map((muscleGroup, index) => (
                <Select.Option
                  key={index}
                  value={muscleGroup.value}
                  label={muscleGroup.label}
                >
                  <span>{muscleGroup.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col md="auto" xs={12} className="my-3">
            <Select
              value={filters.motorPatternId}
              placeholder="Cerca per pattern motorio..."
              className="w-100"
              onChange={handleFieldChange("motorPatternId")}
            >
              {(motorPatterns || []).map((motorPatterns, index) => (
                <Select.Option
                  key={index}
                  value={motorPatterns.value}
                  label={motorPatterns.label}
                >
                  <span>{motorPatterns.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row className="mb-4 align-items-center justify-content-center">
          <ConfigProvider locale={itIT}>
            <Pagination
              disabled={!exercises}
              total={total || 0}
              showTotal={(t, range) => `${range[0]}-${range[1]} di ${t}`}
              pageSize={filters.pageSize}
              current={filters.currentPage}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              showSizeChanger
              align="center"
            />
          </ConfigProvider>
        </Row>
      </>
    );
  }, [
    debounceSearchChange,
    filters,
    handleFieldChange,
    motorPatterns,
    muscleGroups,
    handleEditExerciseClick,
    exercises,
    total,
    handlePageChange,
    handlePageSizeChange,
  ]);

  const handleExerciseSubmit = useCallback(
    async (values: any, file?: any) => {
      setLoading(true);

      const fd = new FormData();
      fd.append("name", values.name);
      if (values.muscleGroupId)
        fd.append("muscleGroupId", values.muscleGroupId);
      if (values.motorPatternId)
        fd.append("motorPatternId", values.motorPatternId);
      fd.append("description", values.description || "");
      // Downscale/compress in the browser before upload so the payload is small
      // and fast; falls back to the original File on any failure.
      if (file) fd.append("imageUrl", await compressImageFile(file));

      const promise = selectedExercise
        ? Entities.updateExercise(selectedExercise.id!, fd)
        : Entities.createExercise(fd);

      promise.then(
        () => {
          setLoading(false);
          handleEditExerciseModalClose();
          showToast(
            `${selectedExercise ? "edit" : "save"}-exercise-success`,
            `Esercizio ${
              selectedExercise ? "modificato" : "salvato"
            } correttamente`,
            undefined,
            "success"
          );
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            `${selectedExercise ? "edit" : "save"}-exercise-error`,
            `Errore nel${selectedExercise ? "la" : ""} ${
              selectedExercise ? "modifica" : "salvataggio"
            } dell'esercizio`,
            undefined,
            "danger"
          );
        }
      );
    },
    [handleEditExerciseModalClose, showToast, reset, selectedExercise]
  );

  const handleDeleteExerciseModalClose = useCallback(() => {
    setSelectedExercise(undefined);
    setDeleteExerciseModal(false);
  }, []);

  const deleteExercise = useCallback(() => {
    setLoading(true);
    Entities.deleteExercise(selectedExercise!.id!).then(
      () => {
        setLoading(false);
        handleDeleteExerciseModalClose();
        showToast(
          `delete-exercise-success`,
          `Esercizio eliminato correttamente`,
          undefined,
          "success"
        );
        reset();
      },
      () => {
        setLoading(false);
        showToast(
          `delete-exercise-error`,
          `Errore nella cancellazione dell'esercizio`,
          undefined,
          "danger"
        );
      }
    );
  }, [showToast, reset, handleDeleteExerciseModalClose, selectedExercise]);

  return (
    <Container fluid="md" className="mt-5">
      {renderToolbar()}
      <hr />
      {renderAsyncContent()}

      <Modal show={addExerciseModal} onHide={handleEditExerciseModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedExercise ? "Modifica" : "Nuovo"} Esercizio
          </Modal.Title>
        </Modal.Header>
        <ExerciseForm
          exercise={selectedExercise}
          onSubmit={handleExerciseSubmit}
          onClose={handleEditExerciseModalClose}
          muscleGroups={muscleGroups}
          motorPatterns={motorPatterns}
          loading={loading}
        />
      </Modal>

      <Modal show={deleteExerciseModal} onHide={handleDeleteExerciseModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Esercizio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare l'Esercizio{" "}
            <strong>{selectedExercise?.name}</strong>?
          </p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteExerciseModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={deleteExercise}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
